import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { CollapsibleLogicSection } from './CollapsibleLogicSection';
import { LogicNodeRenderer } from './LogicNodeRenderer';
import { useConceptGroupMaps } from '../../hooks/useConceptGroupMaps';
import { useNodeHasRecentlyApplied } from '../../hooks/useNodeHasRecentlyApplied';
import type { LogicNode } from '../../utils/analyticalVariable.utils';
import {
  collectAllConceptNamesFromLogicTree,
  getNodeLabel,
  getUnresolvedConceptNames,
} from '../../utils/analyticalVariable.utils';
import { useAnalyticalVariableCardContext } from '../AnalyticalVariableCard/AnalyticalVariableCardContext';

interface CollapsibleLogicNodeProps {
  node: LogicNode;
  onChipClick: (name: string) => void;
  badge?: ReactNode;
  labelOverride?: string;
  'data-testid'?: string;
}

/**
 * Renders any LogicNode wrapped in a CollapsibleLogicSection with its derived label.
 * Used by FilterGroupRenderer and TemporalGroupRenderer to display child nodes
 * with a consistent collapsible header.
 *
 * Read-only mode: sections are collapsed by default (headers visible, details hidden).
 * Edit mode: sections are expanded by default.
 */
export const CollapsibleLogicNode = ({
  node,
  onChipClick,
  badge,
  labelOverride,
  'data-testid': dataTestId,
}: CollapsibleLogicNodeProps) => {
  const intl = useIntl();
  const { analyticalVariable, isEditable, conceptGroups } = useAnalyticalVariableCardContext();
  const { idToNameMap, nameToOwnerMap } = useConceptGroupMaps(conceptGroups);

  const names = useMemo(
    () => collectAllConceptNamesFromLogicTree(node, idToNameMap),
    [node, idToNameMap]
  );

  const hasError = useMemo(
    () => getUnresolvedConceptNames(names, nameToOwnerMap).length > 0,
    [names, nameToOwnerMap]
  );

  const hasRecentlyApplied = useNodeHasRecentlyApplied(analyticalVariable.id, names);

  return (
    <CollapsibleLogicSection
      label={labelOverride ?? getNodeLabel(intl, node)}
      badge={badge}
      defaultOpen={isEditable || hasError || hasRecentlyApplied}
      hasError={hasError}
      data-testid={dataTestId}
    >
      <LogicNodeRenderer node={node} onChipClick={onChipClick} />
    </CollapsibleLogicSection>
  );
};
